import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useSlotBattle } from '../../../../hooks/useSlotBattle';
import { SLOT_BATTLE_BUTTON_TEXT, trackSlotBattle } from '../../../../stores/slot-battle/slotBattleAnalytics';
import { useStores } from '../../../../stores/useStores';
import { useCountdownTimer } from '../../hooks/useCountdownTimer';
import type { SlotBattleWidgetDataType, WidgetSizeType } from '../../types';
import { GeneralSmallWidget, GeneralWideWidget, GeneralWidgetTimer } from '../GeneralWidget';
import { WidgetWrapper } from '../WidgetWrapper/WidgetWrapper';

interface SlotBattleWidgetProps {
  sizeType: WidgetSizeType;
  data: SlotBattleWidgetDataType;
}

/**
 * Lobby rail entry for Battle of the Slots. Display and navigation only — it opens the one lobby
 * dialog and never writes participation: receiving the event trigger IS the opt-in.
 *
 * The artwork is `battle_run.widget_image_url`, authored per run in the admin tool, so there is no
 * bundled asset to fall back to. No image means no widget rather than an empty plate.
 */
function SlotBattleWidgetComponent({ data, sizeType }: SlotBattleWidgetProps) {
  const { slotBattleStore } = useStores();
  const { t } = useTranslation();
  const battle = useSlotBattle();

  const { timer, isExpired } = useCountdownTimer({
    endDate: battle?.endDate ?? '',
    onEnd: () => {
      // The run's own end is not the widget's end — the closing pane lives for 7 more days — so this
      // only refreshes to pick up the settled state.
      void slotBattleStore.fetch({ force: true });
    },
  });

  if (!battle?.widgetImageUrl) {
    return null;
  }

  const showTimer = battle.state === 'live' && !isExpired;

  const handleClick = () => {
    // The small tile has no CTA, so only the wide widget reports a button_text.
    trackSlotBattle('widget', battle, {
      event_action: 'click',
      click_location: 'lobby',
      ...(sizeType === 'small' ? {} : { button_text: SLOT_BATTLE_BUTTON_TEXT.letsGo }),
    });
    slotBattleStore.openDialog('widget');
  };

  return (
    <WidgetWrapper dataTestId={data.dataTestId} sizeType={sizeType} onClick={handleClick}>
      {sizeType === 'small' ? (
        <GeneralSmallWidget
          timer={
            showTimer ? <GeneralWidgetTimer dataTestId={`${data.dataTestId}-timer`} timerValue={timer} /> : undefined
          }
          widgetImageUrl={battle.widgetImageUrl}
        />
      ) : (
        <GeneralWideWidget
          ctaLabel={t('slotBattle.widgetCta')}
          timer={
            showTimer ? <GeneralWidgetTimer dataTestId={`${data.dataTestId}-timer`} timerValue={timer} /> : undefined
          }
          widgetImageUrl={battle.widgetImageUrl}
        />
      )}
    </WidgetWrapper>
  );
}

export const SlotBattleWidget = observer(SlotBattleWidgetComponent);
